/**
 * The notifications console — `/v1/admin/notifications/*`.
 *
 * **The catalogue is read-only, and the hooks say so by having no mutation.** It is code: a new
 * event is a backend change. A `useCreateEvent` that posted somewhere would be a promise the
 * server cannot keep.
 *
 * **Activating a template invalidates the catalogue too.** Coverage gaps are computed from which
 * templates are active, so a screen that refreshed the list but not the gaps would show a hole
 * that had just been filled — and somebody would go and fill it twice.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type CatalogueEventRow = components['schemas']['CatalogueEventRow'];
export type CatalogueGapRow = components['schemas']['CatalogueGapRow'];
export type TemplateRow = components['schemas']['TemplateRow'];
export type QuietHoursRow = components['schemas']['QuietHoursRow'];
export type MessageRow = components['schemas']['MessageRow'];
type NotificationCatalogue = components['schemas']['NotificationCatalogue'];
type TemplateList = components['schemas']['TemplateList'];
export type TemplateIn = components['schemas']['TemplateIn'];
export type TemplatePreview = components['schemas']['TemplatePreview'];
export type TemplateTestSend = components['schemas']['TemplateTestSend'];
type QuietHoursList = components['schemas']['QuietHoursList'];
type MessagePage = components['schemas']['MessagePage'];
export type SuppressionRow = components['schemas']['SuppressionRow'];
type SuppressionPage = components['schemas']['SuppressionPage'];
export type SuppressionIn = components['schemas']['SuppressionIn'];
export type GatewayRoute = components['schemas']['GatewayRoute'];
type GatewayRoutes = components['schemas']['GatewayRoutes'];
export type DeliverabilityRow = components['schemas']['DeliverabilityRow'];
type Deliverability = components['schemas']['Deliverability'];

export interface MessageQuery {
  user_account_id?: number;
  channel?: string;
  status?: string;
  event_type?: string;
  page?: number;
  page_size?: number;
}

/** Only what the operator actually set — an empty `status=` narrows to nothing. */
export function messageParams(query: MessageQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export function useNotificationCatalogue() {
  return useQuery({
    queryKey: keys.notifications.catalogue(),
    queryFn: () =>
      authedClient.request<NotificationCatalogue>('/v1/admin/notifications/catalogue'),
  });
}

/**
 * Every version of every template. Unfiltered on purpose — the list is complete and unpaged, so
 * the screen's search box narrows what is already in hand rather than one page of an unknown
 * many. The endpoint takes filters; the moment this list is paged, they move here.
 */
export function useNotificationTemplates() {
  return useQuery({
    queryKey: keys.notifications.templates({}),
    queryFn: () => authedClient.request<TemplateList>('/v1/admin/notifications/templates'),
  });
}

export function useQuietHours() {
  return useQuery({
    queryKey: keys.notifications.quietHours(),
    queryFn: () => authedClient.request<QuietHoursList>('/v1/admin/notifications/quiet-hours'),
  });
}

export function useNotificationMessages(query: MessageQuery) {
  const params = messageParams(query);
  return useQuery({
    queryKey: keys.notifications.messages(query as Readonly<Record<string, unknown>>),
    queryFn: () =>
      // Always a query string: the screen sets `page` and `page_size` on every call, because
      // an unpaged request to a paged endpoint is one whose answer size nobody chose.
      authedClient.request<MessagePage>(`/v1/admin/notifications/messages?${params}`),
  });
}

/**
 * Every write invalidates the templates AND the catalogue.
 *
 * The gaps are derived from which templates are active, so refreshing one without the other
 * would leave a hole on screen that had just been filled — and somebody would fill it twice.
 */
function useTemplateMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.notifications.all() });
    },
  });
}

export function useActivateTemplate() {
  return useTemplateMutation((templateId: number) =>
    authedClient.request<TemplateRow>(
      `/v1/admin/notifications/templates/${templateId}/activate`,
      { method: 'POST' },
    ),
  );
}

export function useDeactivateTemplate() {
  return useTemplateMutation((templateId: number) =>
    authedClient.request<TemplateRow>(
      `/v1/admin/notifications/templates/${templateId}/deactivate`,
      { method: 'POST' },
    ),
  );
}

export function useSetQuietHours() {
  return useTemplateMutation(
    ({
      policyId,
      ...body
    }: {
      policyId: number;
      start_time: string;
      end_time: string;
      behaviour: string;
      is_active: boolean;
    }) =>
      authedClient.request<QuietHoursRow>(`/v1/admin/notifications/quiet-hours/${policyId}`, {
        method: 'PUT',
        body,
      }),
  );
}

/** A new version, always a draft — activation stays a separate act (doc 05-admin/26 §3.1). */
export function useCreateTemplate() {
  return useTemplateMutation((body: TemplateIn) =>
    authedClient.request<TemplateRow>('/v1/admin/notifications/templates', { method: 'POST', body }),
  );
}

/** What a version renders to, through the same sandbox and strict undefined a real send uses. */
export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (args: { templateId: number; context: Record<string, string> }) =>
      authedClient.request<TemplatePreview>(`/v1/admin/notifications/templates/${args.templateId}/preview`, {
        method: 'POST',
        body: { context: args.context },
      }),
  });
}

/** Send one version to the environment's override address (admin doc 26 §3.1 item 5). The server
    refuses with `no_override_address` where none is configured — production — and for push and
    WhatsApp, which have none; a provider that failed is a result (`sent: false`), not an error. */
export function useTestSendTemplate() {
  return useMutation({
    mutationFn: (args: { templateId: number; context: Record<string, string> }) =>
      authedClient.request<TemplateTestSend>(`/v1/admin/notifications/templates/${args.templateId}/test-send`, {
        method: 'POST',
        body: { context: args.context },
      }),
  });
}

/** The suppression list (admin doc 26 §3.3): bounces, spam reports, STOPs, manual entries. */
export function useSuppressions(channel: string, page: number, pageSize: number) {
  const params = messageParams({ channel, page, page_size: pageSize });
  return useQuery({
    queryKey: keys.notifications.suppressions(channel, page),
    queryFn: () =>
      authedClient.request<SuppressionPage>(`/v1/admin/notifications/suppressions?${params}`),
  });
}

/** Suppress a person's current address on one channel; the note lands in the audit log. */
export function useAddSuppression() {
  return useTemplateMutation((body: SuppressionIn) =>
    authedClient.request<SuppressionRow>('/v1/admin/notifications/suppressions', {
      method: 'POST',
      body,
    }),
  );
}

/** Lifting re-exposes an address that bounced or complained, so it carries a reason. */
export function useRemoveSuppression() {
  return useTemplateMutation(({ id, reason }: { id: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/notifications/suppressions/${id}/remove`, {
      method: 'POST',
      body: { reason },
    }),
  );
}

/** Each channel's senders in the order they are tried, `log` last (arch-11 §11). */
export function useGatewayRoutes() {
  return useQuery({
    queryKey: keys.notifications.gateways(),
    queryFn: () => authedClient.request<GatewayRoutes>('/v1/admin/notifications/gateways'),
  });
}

/** Superadmin only on the server; growth gets a 403 it can read. */
export function useChangeGatewayRoute() {
  return useTemplateMutation(
    (args: { channel: string; name: string; enabled: boolean; priority: number; reason: string }) =>
      authedClient.request<GatewayRoutes>(
        `/v1/admin/notifications/gateways/${args.channel}/${args.name}`,
        {
          method: 'PUT',
          body: { enabled: args.enabled, priority: args.priority, reason: args.reason },
        },
      ),
  );
}

/** Per-channel sends and receipts over a window, with the alarm lines (admin doc 26 §3.5 item 2). */
export function useDeliverability(days: number) {
  return useQuery({
    queryKey: keys.notifications.deliverability(days),
    queryFn: () =>
      authedClient.request<Deliverability>(`/v1/admin/notifications/deliverability?days=${days}`),
  });
}
